import { useEffect } from 'react';
import { DEFAULT_DESCRIPTION, SITE_NAME, SITE_URL, fullTitle } from './site';

const STRUCTURED_DATA_ID = 'page-structured-data';

interface PageMeta {
  title?: string;
  description?: string;
  // Canonical path on the production domain, e.g. "/event/12". Omit for pages
  // search engines shouldn't index.
  canonicalPath?: string;
  // schema.org JSON-LD, e.g. a SportsEvent.
  structuredData?: object;
  // Absolute URL of the image for link previews.
  image?: string;
}

const upsertTag = (selector: string, create: () => HTMLElement): HTMLElement => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  const el = upsertTag(`meta[${attr}="${key}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    return meta;
  });
  el.setAttribute('content', content);
};

// Sets the document title, description, canonical link and structured data
// for a page. Google reads these after rendering the page's JavaScript.
export const usePageMeta = ({ title, description, canonicalPath, structuredData, image }: PageMeta) => {
  const structuredDataJSON = structuredData ? JSON.stringify(structuredData) : '';

  useEffect(() => {
    const pageTitle = fullTitle(title);
    const desc = description || DEFAULT_DESCRIPTION;

    document.title = pageTitle;
    setMeta('name', 'description', desc);
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:site_name', SITE_NAME);
    if (image) {
      setMeta('property', 'og:image', image);
    }

    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonicalPath) {
      const link = upsertTag('link[rel="canonical"]', () => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        return el;
      });
      link.setAttribute('href', `${SITE_URL}${canonicalPath}`);
      setMeta('property', 'og:url', `${SITE_URL}${canonicalPath}`);
    } else {
      canonical?.remove();
    }

    document.getElementById(STRUCTURED_DATA_ID)?.remove();
    if (structuredDataJSON) {
      const script = document.createElement('script');
      script.id = STRUCTURED_DATA_ID;
      script.type = 'application/ld+json';
      script.textContent = structuredDataJSON;
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(STRUCTURED_DATA_ID)?.remove();
    };
  }, [title, description, canonicalPath, structuredDataJSON, image]);
};
